import { Button, Col, GetProp, Radio, Select, Upload, UploadProps } from "antd";
import useForm from "../../../hooks/useForm";
import { CloseModal } from "../../../hooks/useModal";
import usePageState from "../../../hooks/usePageState";
import AppInputNumber from "../../../components/AppInputNumber";
import AppInputText from "../../../components/AppInputText/AppInputText";
import BaseDialog from "../../../components/BaseDialog";
import Column from "../../../components/Column";
import DialogFooter from "../../../components/DialogFooter";
import Row from "../../../components/Row";
import Title from "../../../components/Title";
import {
  ALL_ITEM_ID,
  RECURRING_TYPE,
  SALE_TYPE_ENUM,
  SALE_VALUE_TYPE_ENUM,
  SALE_VALUE_TYPE_OPTION,
  SIZE_MAPPING_ENUM,
  SIZE_MAPPING_OPTION,
  WEEK_DAYS,
  YES_NO_DATA,
  YES_NO_ENUM,
} from "../../../../applications/constants/AppConst";
import * as Yup from "yup";
import { DeleteOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { useState } from "react";
import {
  ComboItemRequest,
  CreateSaleInfoRequest,
  SaleInfoModel,
  SaleInfoResponse,
} from "../../../../infrastructure/model/SaleInfoModel";
import AppInputDate from "../../../components/AppInputDate";
import Visible from "../../../components/Visible";
import AppInputDropdownMultiple from "../../../components/AppDropdownMultiple";
import { useItemsContext } from "../../../context/AppItemsContext";
import AppInputDropdown from "../../../components/AppInputDropdown";
import AppDropdown from "../../../components/AppDropdown";
import { validateNewPrice } from "../../../../applications/useCase/BusinessUseCase/ConvertNoteUseCase";
import {
  createSaleUseCase,
  updateSaleUseCase,
} from "../../../../applications/useCase/ApiUseCase/SaleApiUseCase";

interface CreateSaleProps {
  onClose: CloseModal;
  updateItem?: SaleInfoModel;
}

type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0];

function CreateSaleInfoDialog(props: CreateSaleProps) {
  const { onClose, updateItem } = props;
  const { showClientError, showError, setLoading, isLoading, showSuccess } = usePageState();
  const [fileImage, setFileImage] = useState<File>();
  const { items } = useItemsContext();

  async function insertItem(request: CreateSaleInfoRequest, imageFile?: File) {
    const invalidMessage = validateNewPrice(request, items);
    if (invalidMessage) {
      showClientError(invalidMessage);
      return;
    }
    let res: SaleInfoResponse | undefined = undefined;
    if (updateItem?.id) {
      res = await updateSaleUseCase({
        request: { ...request, id: updateItem.id, thumbnailImage: imageFile },
        setLoading,
        showError,
      });
    } else {
      res = await createSaleUseCase({
        request,
        thumbnailImage: imageFile,
        setLoading,
        showError,
      });
    }
    if (res) {
      onClose(res);
    }
  }

  const validationSchema = Yup.object({
    title: Yup.string().required("Thiếu tiêu đề khuyến mãi"),
    content: Yup.string().required("Thiếu nội dung chương trình khuyến mãi"),
    is_recurring: Yup.boolean(),
    sale_start_date: Yup.number().when("is_recurring", {
      is: false,
      then: (schema) =>
        schema.test(
          "required-start-date",
          "Ngày bắt đầu là bắt buộc",
          (value) => typeof value === "number" && Number.isFinite(value) && value > 0
        ),
      otherwise: (schema) => schema.notRequired(),
    }),

    sale_end_date: Yup.number().when("is_recurring", {
      is: false,
      then: (schema) =>
        schema
          .test(
            "required-end-date",
            "Vui lòng nhập ngày kết thúc",
            (value) => typeof value === "number" && Number.isFinite(value) && value > 0
          )
          .test("end-after-start", "Ngày kết thúc phải sau ngày bắt đầu", function (value) {
            const { sale_start_date } = this.parent;
            if (!value || !sale_start_date) return true;
            return value > sale_start_date;
          }),
      otherwise: (schema) => schema.notRequired(),
    }),

    recurring_days: Yup.array().when("is_recurring", {
      is: true,
      then: (schema) => schema.min(1, "Vui lòng chọn ít nhất 1 ngày trong tuần"),
      otherwise: (schema) => schema.notRequired(),
    }),
    sale_order_condition: Yup.number().when("sale_type", {
      is: (val: number) => val !== SALE_TYPE_ENUM.COMBO,
      then: (schema) => schema.required("Thiếu điều kiện thỏa mãn khuyến mãi"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const formik = useForm<CreateSaleInfoRequest>({
    initialValues: updateItem?.id
      ? {
          ...updateItem,
          sale_type: updateItem?.sale_type,
          sale_value_type: updateItem?.sale_value_type,
        }
      : {
          title: "",
          content: "",
          apply_for: [],
          apply_for_size: [],
          sale_type: SALE_TYPE_ENUM.ALL_BILL,
          sale_end_date: 0,
          sale_start_date: 0,
          sale_value: 0,
          sale_order_condition: undefined,
          sale_value_type: SALE_VALUE_TYPE_ENUM.VND,
          new_price_temp: 0,
          auto_apply_for_user: false,
          applicable_user_count: undefined,
          combo_items: [],
          is_recurring: false,
          recurring_days: [],
          max_discount_value: 0,
          minimum_orders_required: 0,
          max_orders_required: 0,
        },
    validationSchema: validationSchema,
    onSubmit: async (values) => await insertItem(values, fileImage),
  });

  const beforeUpload = (file: FileType) => {
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      showClientError("Image must smaller than 5MB!");
      return false;
    }
    setFileImage(file);
    return false;
  };

  const selectCurrencyAfter = (ignoreCoc?: boolean) => {
    const options = ignoreCoc
      ? SALE_VALUE_TYPE_OPTION?.filter((_, i) => i !== 2)
      : SALE_VALUE_TYPE_OPTION;
    return (
      <Select
        defaultValue={
          formik.values.sale_value_type ? formik.values.sale_value_type : SALE_VALUE_TYPE_ENUM.VND
        }
        style={{ width: 80 }}
        onChange={(e) => formik.updateFieldValue("sale_value_type", e)}
      >
        {options?.map((s, index) => {
          return <Select.Option value={s.value}>{s.label}</Select.Option>;
        })}
      </Select>
    );
  };

  const handleAddComboItem = () => {
    const oldValue = formik.values.combo_items || [];
    const newValue: ComboItemRequest[] = [
      ...oldValue,
      {
        item_id: 0,
        item_size: SIZE_MAPPING_ENUM.S,
      },
    ];
    formik.updateFieldValue("combo_items", newValue);
  };

  const handleUpdateComboItem = (index: number, key: keyof ComboItemRequest, value: number) => {
    const oldValue = formik.values.combo_items || [];
    const newValue: ComboItemRequest[] = [
      ...oldValue.slice(0, index),
      {
        ...oldValue[index],
        [key]: value,
      },
      ...oldValue.slice(index + 1),
    ];

    formik.updateFieldValue("combo_items", newValue);
  };

  const handleDeleteComboItem = (index: number) => {
    const oldValue = formik.values.combo_items || [];
    const newValue = oldValue.filter((_, i) => i !== index);
    formik.updateFieldValue("combo_items", newValue);
  };

  return (
    <BaseDialog
      title={updateItem?.id ? "Cập nhật tin tức khuyến mãi" : "Thêm mới khuyến mãi"}
      isLoading={isLoading}
      width={"50vw"}
      footer={
        <DialogFooter
          submitProps={{
            onClick: () => formik.checkErrorAndSubmit(showClientError),
          }}
          cancelProps={{
            onClick: () => onClose(),
          }}
        />
      }
    >
      <Column className="gap-2 w-100">
        <Row>
          <Title label="Thumbnail" />
          <Row className="gap-2">
            <Upload
              name="avatar_item"
              className="flex flex-row avatar-uploader"
              accept="image/*"
              showUploadList={false}
              maxCount={1}
              beforeUpload={beforeUpload}
              action={undefined}
            >
              <Button icon={<UploadOutlined />} type="primary">
                Upload
              </Button>
            </Upload>
            <div
              style={{
                maxWidth: `250px`,
                minWidth: "250px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                wordBreak: "break-word",
              }}
            >
              {fileImage?.name}
            </div>
          </Row>
        </Row>
        <AppInputText
          label="Tiêu đề"
          currentValue={formik.values.title}
          isRequired
          onTextChange={(e) => formik.updateFieldValue("title", e)}
          errorText={formik.getFormErrorMessage("title")}
        />
        <AppInputDropdown
          label="Loại khuyến mãi"
          currentValue={
            formik.values.is_recurring ? RECURRING_TYPE.RECURRING : RECURRING_TYPE.ONE_TIME
          }
          onChangeValue={(e) => {
            const isRecurring = e === RECURRING_TYPE.RECURRING;

            formik.updateFieldValue("is_recurring", isRecurring);

            if (isRecurring) {
              // clear one-time fields
              formik.updateFieldValue("sale_start_date", 0);
              formik.updateFieldValue("sale_end_date", 0);
            } else {
              // clear recurring fields
              formik.updateFieldValue("recurring_days", []);
            }
          }}
          options={[
            { label: "Khuyến mãi theo thời gian", value: RECURRING_TYPE.ONE_TIME },
            { label: "Khuyến mãi lặp theo tuần", value: RECURRING_TYPE.RECURRING },
          ]}
        />
        <Visible isVisible={!formik.values.is_recurring}>
          <AppInputDate
            label="Ngày bắt đầu"
            currentValue={formik.values.sale_start_date}
            isRequired
            isShowTime={false}
            onChangeValue={(e) => formik.updateFieldValue("sale_start_date", e!)}
            errorText={formik.getFormErrorMessage("sale_start_date")}
          />
          <AppInputDate
            label="Ngày kết thúc"
            currentValue={formik.values.sale_end_date}
            isRequired
            isShowTime={false}
            onChangeValue={(e) => formik.updateFieldValue("sale_end_date", e!)}
            errorText={formik.getFormErrorMessage("sale_end_date")}
          />
        </Visible>
        <Visible isVisible={formik.values.is_recurring}>
          <AppInputDropdownMultiple
            label="Áp dụng theo ngày trong tuần"
            isRequired
            options={WEEK_DAYS}
            currentValue={formik.values.recurring_days}
            onChangeValue={(e) => formik.updateFieldValue("recurring_days", e)}
            errorText={formik.getFormErrorMessage("recurring_days")}
          />
        </Visible>
        <AppInputDropdown
          label="Áp dụng tự động "
          currentValue={formik.values.auto_apply_for_user ? YES_NO_ENUM.YES : YES_NO_ENUM.NO}
          onChangeValue={(e) =>
            formik.updateFieldValue("auto_apply_for_user", e === YES_NO_ENUM.YES)
          }
          options={YES_NO_DATA}
        />
        <AppInputNumber
          label="Số lượng user được áp dụng"
          className="flex-1 w-100"
          currentValue={formik.values.applicable_user_count}
          onChangeValue={(e) => formik.updateFieldValue("applicable_user_count", e)}
        />
        <Row className="justify-content-center mt-2 mb-2">
          <Radio
            checked={formik.values.sale_type === SALE_TYPE_ENUM.NEW_USER}
            onChange={(e) => formik.updateFieldValue("sale_type", SALE_TYPE_ENUM.NEW_USER)}
          >
            Áp dụng cho đơn onl
          </Radio>
          <Radio
            checked={formik.values.sale_type === SALE_TYPE_ENUM.ALL_BILL}
            onChange={(e) => formik.updateFieldValue("sale_type", SALE_TYPE_ENUM.ALL_BILL)}
          >
            Áp dụng cho tổng đơn
          </Radio>
          <Radio
            checked={formik.values.sale_type === SALE_TYPE_ENUM.EACH_ITEM}
            onChange={(e) => formik.updateFieldValue("sale_type", SALE_TYPE_ENUM.EACH_ITEM)}
          >
            Áp dụng cho từng sản phẩm
          </Radio>
          <Radio
            checked={formik.values.sale_type === SALE_TYPE_ENUM.COMBO}
            onChange={(e) => formik.updateFieldValue("sale_type", SALE_TYPE_ENUM.COMBO)}
          >
            Áp dụng cho combo
          </Radio>
        </Row>
        <Visible
          isVisible={
            formik.values.sale_type === SALE_TYPE_ENUM.ALL_BILL ||
            formik.values.sale_type === SALE_TYPE_ENUM.NEW_USER
          }
        >
          <AppInputNumber
            label="Giảm giá"
            className="flex-1 w-100"
            currentValue={formik.values.sale_value}
            onChangeValue={(e) => formik.updateFieldValue("sale_value", e)}
            min={0}
            errorText={formik.getFormErrorMessage("sale_value")}
            addonAfter={selectCurrencyAfter(true)}
          />

          <AppInputNumber
            label="Đơn tối thiểu"
            className="flex-1 w-100"
            placeholder="Đơn hàng có tổng giá trị tối thiểu..."
            currentValue={formik.values.sale_order_condition}
            onChangeValue={(e) => formik.updateFieldValue("sale_order_condition", e)}
            isRequired
            errorText={formik.getFormErrorMessage("sale_order_condition")}
            suffix={"VNĐ"}
            min={0}
          />
          <AppInputNumber
            label="Giảm giá tối đa"
            className="flex-1 w-100"
            currentValue={formik.values.max_discount_value}
            onChangeValue={(e) => formik.updateFieldValue("max_discount_value", e)}
            min={0}
            errorText={formik.getFormErrorMessage("max_discount_value")}
            suffix={"VNĐ"}
          />
          <Visible isVisible={formik.values.sale_type === SALE_TYPE_ENUM.NEW_USER}>
            <AppInputNumber
              label="SL đơn đã đặt tối thiểu"
              className="flex-1 w-100"
              placeholder="Áp dụng cho khách hàng đã đặt tối thiểu N đơn ..."
              currentValue={formik.values.minimum_orders_required}
              onChangeValue={(e) => formik.updateFieldValue("minimum_orders_required", e)}
              errorText={formik.getFormErrorMessage("minimum_orders_required")}
              suffix={"Đơn"}
              min={0}
            />
            <AppInputNumber
              label="SL đơn đã đặt tối đa"
              className="flex-1 w-100"
              placeholder="Áp dụng cho khách hàng đã đặt tối đa N đơn ..."
              currentValue={formik.values.max_orders_required}
              onChangeValue={(e) => formik.updateFieldValue("max_orders_required", e)}
              errorText={formik.getFormErrorMessage("max_orders_required")}
              suffix={"Đơn"}
              min={0}
            />
          </Visible>
        </Visible>
        <Visible isVisible={formik.values.sale_type === SALE_TYPE_ENUM.EACH_ITEM}>
          <AppInputDropdownMultiple
            label="Áp dụng cho mặt hàng"
            isRequired
            options={[{ name: "Tất cả sản phẩm", id: ALL_ITEM_ID }, ...items]?.map((e) => {
              return {
                label: e.name,
                value: e.id,
                disabled:
                  formik.values.apply_for?.includes(ALL_ITEM_ID) && e.id !== ALL_ITEM_ID
                    ? true
                    : false,
              };
            })}
            currentValue={formik.values.apply_for}
            onChangeValue={(e) => {
              if (e?.includes(ALL_ITEM_ID)) {
                formik.updateFieldValue("apply_for", [ALL_ITEM_ID]);
              } else {
                formik.updateFieldValue("apply_for", e);
              }
            }}
            errorText={formik.getFormErrorMessage("apply_for")}
          />
          <AppInputDropdownMultiple
            label="Áp dụng cho size"
            isRequired
            options={SIZE_MAPPING_OPTION}
            currentValue={formik.values.apply_for_size}
            onChangeValue={(e) => {
              formik.updateFieldValue("apply_for_size", e);
            }}
            errorText={formik.getFormErrorMessage("apply_for_size")}
          />
          <AppInputNumber
            label="SL SP tối thiểu"
            className="flex-1 w-100"
            placeholder="Áp dụng cho sản phẩm được đặt với tối thiểu N cốc ..."
            currentValue={formik.values.sale_order_condition}
            onChangeValue={(e) => formik.updateFieldValue("sale_order_condition", e)}
            isRequired
            errorText={formik.getFormErrorMessage("sale_order_condition")}
            suffix={"Cốc"}
            min={0}
          />
          <div style={{ fontSize: "12px", fontStyle: "italic", color: "#08F" }}>
            Chỉ áp dụng giảm giá hoặc dùng giá mới tạm thời, không thiết lập cả 2 đồng thời
          </div>
          <AppInputNumber
            label="Giảm giá"
            className="flex-1 w-100"
            currentValue={formik.values.sale_value}
            onChangeValue={(e) => formik.updateFieldValue("sale_value", e)}
            min={0}
            disabled={formik.values.new_price_temp! > 0}
            errorText={formik.getFormErrorMessage("sale_value")}
            addonAfter={selectCurrencyAfter(false)}
          />
          <AppInputNumber
            label="Giá mới tạm thời"
            className="flex-1 w-100"
            currentValue={formik.values.new_price_temp}
            onChangeValue={(e) => formik.updateFieldValue("new_price_temp", e)}
            min={0}
            disabled={formik.values.sale_value! > 0}
            errorText={formik.getFormErrorMessage("new_price_temp")}
            addonAfter={"VNĐ"}
          />
        </Visible>
        <Visible isVisible={formik.values.sale_type === SALE_TYPE_ENUM.COMBO}>
          <AppInputNumber
            label="Giá combo"
            className="flex-1 w-100"
            currentValue={formik.values.sale_value}
            onChangeValue={(e) => formik.updateFieldValue("sale_value", e)}
            min={0}
            errorText={formik.getFormErrorMessage("sale_value")}
            addonAfter={"VNĐ"}
          />
          <AppInputNumber
            label="Áp dụng tối thiểu"
            className="flex-1 w-100"
            placeholder="Áp dụng cho sản phẩm được đặt với tối thiểu N cốc ..."
            currentValue={formik.values.sale_order_condition}
            onChangeValue={(e) => formik.updateFieldValue("sale_order_condition", e)}
            errorText={formik.getFormErrorMessage("sale_order_condition")}
            suffix={"Cốc"}
            min={0}
          />
          <Row>
            <Col span={4}>
              <Button
                type="primary"
                variant="solid"
                color="primary"
                icon={<PlusOutlined />}
                onClick={() => handleAddComboItem()}
              >
                SP
              </Button>
            </Col>
            <Col span={20}>
              {formik.values.combo_items?.map((cb, index) => {
                const currentItemSelected = items.find((e) => e.id === cb.item_id);
                const itemSizes = currentItemSelected?.size_mapping?.map((s) => s.size);
                return (
                  <Row className="gap-2 mb-2">
                    <Col span={16}>
                      <AppDropdown
                        label="Sản phẩm"
                        className="m-1 w-100"
                        placeholder="Chọn sản phẩm"
                        currentValue={cb.item_id}
                        onChangeValue={(e) => {
                          handleUpdateComboItem(index, "item_id", e);
                        }}
                        options={items?.map((e) => {
                          return { label: e.name, value: e.id };
                        })}
                      />
                    </Col>
                    <Col span={6}>
                      <AppDropdown
                        className="m-1 w-100"
                        placeholder="Chọn size"
                        options={SIZE_MAPPING_OPTION?.filter((e) => itemSizes?.includes(e.value))}
                        currentValue={cb.item_size}
                        onChangeValue={(e) => {
                          handleUpdateComboItem(index, "item_size", e);
                        }}
                        disabled={!cb.item_id}
                      />
                    </Col>
                    <Col span={2}>
                      <Button
                        className="mt-1"
                        type="primary"
                        variant="solid"
                        color="danger"
                        onClick={() => handleDeleteComboItem(index)}
                        icon={<DeleteOutlined />}
                      ></Button>
                    </Col>
                  </Row>
                );
              })}
            </Col>
          </Row>
        </Visible>

        <AppInputText
          label="Nội dung KM"
          isRequired
          textType="MULTI_LINE"
          currentValue={formik.values.content}
          onTextChange={(e) => formik.updateFieldValue("content", e)}
          additionProps={{
            rows: 10,
          }}
        />
      </Column>
    </BaseDialog>
  );
}
export default CreateSaleInfoDialog;
